/**
 * EditDatasetConfigModal — Carbon modal to edit an existing Dataset's
 * metadata (name, description) and column schema.
 *
 * The schema editor is a full-featured inline editor with:
 *   - Key (snake_case), display label, dtype, unit, role (feature/target)
 *   - Optional extraction hint / description per column
 *   - Add / remove / reorder columns
 *
 * After saving the user is offered to rebuild immediately (since the new
 * schema may require re-extraction). Existing rows that no longer match
 * the schema are preserved but warned about.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  Toggle,
  Stack,
} from '@carbon/react';
import { Add, TrashCan, ArrowUp, ArrowDown } from '@carbon/icons-react';
import type { Dataset, DatasetColumn } from '@snxt/snxt-onto-share';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  dataset: Dataset | null;
  onClose: () => void;
  /** Called with the validated dto + whether to rebuild immediately. */
  onSave: (dto: {
    name: string;
    description?: string;
    schema: DatasetColumn[];
  }, rebuild: boolean) => void;
  loading?: boolean;
  error?: string | null;
}

const COLUMN_DTYPES: DatasetColumn['dtype'][] = ['number', 'text', 'boolean'];

function emptyCol(): DatasetColumn {
  return { key: '', label: '', role: 'feature', dtype: 'number', unit: '', description: '' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EditDatasetConfigModal({ open, dataset, onClose, onSave, loading, error }: Props) {
  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [columns,     setColumns]     = useState<DatasetColumn[]>([emptyCol()]);
  const [rebuild,     setRebuild]     = useState(false);
  const [errors,      setErrors]      = useState<Record<string, string>>({});
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  // Sync state when the modal opens with a different dataset
  useEffect(() => {
    if (open && dataset) {
      setName(dataset.name);
      setDescription(dataset.description ?? '');
      setColumns(
        dataset.schema.length > 0
          ? dataset.schema.map(c => ({ ...c, description: c.description ?? '' }))
          : [emptyCol()]
      );
      setRebuild(false);
      setErrors({});
      setExpandedIdx(null);
    }
  }, [open, dataset?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function addColumn() {
    setColumns(cs => [...cs, emptyCol()]);
    setExpandedIdx(columns.length); // expand the new row
  }

  function removeColumn(idx: number) {
    setColumns(cs => cs.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
  }

  function moveColumn(idx: number, dir: -1 | 1) {
    setColumns(cs => {
      const next = [...cs];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return cs;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
    if (expandedIdx === idx) setExpandedIdx(idx + dir);
  }

  function updateColumn(idx: number, patch: Partial<DatasetColumn>) {
    setColumns(cs => cs.map((c, i) => i === idx ? { ...c, ...patch } : c));
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (columns.length === 0) errs.schema = 'At least one column is required';
    columns.forEach((c, i) => {
      if (!c.key.trim())   errs[`col_key_${i}`]   = 'Key required';
      if (!c.label.trim()) errs[`col_label_${i}`] = 'Label required';
    });
    // Duplicate key check
    const keys = columns.map(c => c.key.trim().toLowerCase());
    keys.forEach((k, i) => {
      if (k && keys.indexOf(k) !== i) errs[`col_key_${i}`] = 'Duplicate key';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSave() {
    if (!validate()) return;
    onSave(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        schema: columns.map(c => ({
          key:         c.key.trim().replace(/\s+/g, '_').toLowerCase(),
          label:       c.label.trim(),
          role:        c.role,
          dtype:       c.dtype,
          unit:        c.unit?.trim() || undefined,
          description: c.description?.trim() || undefined,
        })),
      },
      rebuild
    );
  }

  const hasExistingRows = (dataset?.rows_total ?? 0) > 0;

  return (
    <Modal
      open={open}
      modalHeading="Edit extraction configuration"
      primaryButtonText={loading ? undefined : rebuild ? 'Save & rebuild' : 'Save'}
      primaryButtonDisabled={loading}
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleSave}
      onSecondarySubmit={onClose}
      size="lg"
    >
      {loading && <InlineLoading description="Saving…" style={{ marginBottom: '1rem' }} />}

      {error && (
        <InlineNotification
          kind="error"
          title="Save failed"
          subtitle={error}
          style={{ marginBottom: '1rem' }}
        />
      )}

      <Stack gap={5}>
        {/* ── Metadata ── */}
        <TextInput
          id="edit-ds-name"
          labelText="Dataset name"
          value={name}
          onChange={e => setName(e.target.value)}
          invalid={!!errors.name}
          invalidText={errors.name}
        />
        <TextArea
          id="edit-ds-desc"
          labelText="Description (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
        />

        {/* ── Schema editor ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, margin: 0 }}>
              Column schema
            </p>
            <Tag type="gray" size="sm">{columns.length} column{columns.length !== 1 ? 's' : ''}</Tag>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.75rem' }}>
            Define the properties the AI agent should extract. Each column key must be unique (snake_case).
            Changing the schema after a build will orphan existing cell values — rebuild is recommended.
          </p>

          {errors.schema && (
            <p style={{ fontSize: '0.75rem', color: 'var(--cds-support-error)', marginBottom: '0.5rem' }}>
              {errors.schema}
            </p>
          )}

          {columns.map((col, idx) => {
            const isExpanded = expandedIdx === idx;
            return (
              <div
                key={idx}
                style={{
                  border: '1px solid var(--cds-border-subtle)',
                  borderRadius: '4px',
                  marginBottom: '0.5rem',
                  background: 'var(--cds-layer)',
                }}
              >
                {/* ── Collapsed row — always visible ── */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 100px 80px 100px auto',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    alignItems: 'flex-end',
                  }}
                >
                  <TextInput
                    id={`ecol-key-${idx}`}
                    labelText={idx === 0 ? 'Key (snake_case)' : ''}
                    placeholder="adhesion_score"
                    value={col.key}
                    onChange={e => updateColumn(idx, { key: e.target.value })}
                    invalid={!!errors[`col_key_${idx}`]}
                    invalidText={errors[`col_key_${idx}`]}
                    size="sm"
                  />
                  <TextInput
                    id={`ecol-label-${idx}`}
                    labelText={idx === 0 ? 'Display label' : ''}
                    placeholder="Adhesion Score"
                    value={col.label}
                    onChange={e => updateColumn(idx, { label: e.target.value })}
                    invalid={!!errors[`col_label_${idx}`]}
                    invalidText={errors[`col_label_${idx}`]}
                    size="sm"
                  />
                  <Select
                    id={`ecol-dtype-${idx}`}
                    labelText={idx === 0 ? 'Type' : ''}
                    value={col.dtype}
                    onChange={e => updateColumn(idx, { dtype: e.target.value as DatasetColumn['dtype'] })}
                    size="sm"
                  >
                    {COLUMN_DTYPES.map(t => (
                      <SelectItem key={t} value={t} text={t} />
                    ))}
                  </Select>
                  <TextInput
                    id={`ecol-unit-${idx}`}
                    labelText={idx === 0 ? 'Unit' : ''}
                    placeholder="MPa"
                    value={col.unit ?? ''}
                    onChange={e => updateColumn(idx, { unit: e.target.value })}
                    size="sm"
                  />
                  <Select
                    id={`ecol-role-${idx}`}
                    labelText={idx === 0 ? 'Role' : ''}
                    value={col.role}
                    onChange={e => updateColumn(idx, { role: e.target.value as DatasetColumn['role'] })}
                    size="sm"
                  >
                    <SelectItem value="feature" text="feature" />
                    <SelectItem value="target"  text="target"  />
                  </Select>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'flex-end', paddingBottom: errors[`col_key_${idx}`] || errors[`col_label_${idx}`] ? '1.25rem' : 0 }}>
                    <Button
                      kind="ghost"
                      size="sm"
                      iconDescription={isExpanded ? 'Collapse' : 'Expand hint'}
                      hasIconOnly
                      renderIcon={isExpanded ? ArrowUp : ArrowDown}
                      onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                      tooltipPosition="bottom"
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      iconDescription="Move up"
                      hasIconOnly
                      renderIcon={ArrowUp}
                      onClick={() => moveColumn(idx, -1)}
                      disabled={idx === 0}
                      tooltipPosition="bottom"
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      iconDescription="Move down"
                      hasIconOnly
                      renderIcon={ArrowDown}
                      onClick={() => moveColumn(idx, 1)}
                      disabled={idx === columns.length - 1}
                      tooltipPosition="bottom"
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      iconDescription="Remove column"
                      hasIconOnly
                      renderIcon={TrashCan}
                      onClick={() => removeColumn(idx)}
                      disabled={columns.length === 1}
                      tooltipPosition="bottom"
                      style={{ color: 'var(--cds-support-error, #da1e28)' }}
                    />
                  </div>
                </div>

                {/* ── Expanded: extraction hint ── */}
                {isExpanded && (
                  <div style={{ padding: '0 0.75rem 0.75rem' }}>
                    <TextArea
                      id={`ecol-desc-${idx}`}
                      labelText="Extraction hint (optional)"
                      placeholder="Describe how the AI should find or compute this value. E.g. 'Cross-hatch adhesion score (0–5 scale, GT ISO 2409)'"
                      value={col.description ?? ''}
                      onChange={e => updateColumn(idx, { description: e.target.value })}
                      rows={2}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <Button
            kind="ghost"
            size="sm"
            renderIcon={Add}
            onClick={addColumn}
            style={{ marginTop: '0.25rem' }}
          >
            Add column
          </Button>
        </div>

        {/* ── Rebuild toggle ── */}
        <div style={{
          padding: '0.75rem 1rem',
          background: 'var(--cds-layer-accent)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          <Toggle
            id="edit-ds-rebuild"
            labelText="Rebuild after saving"
            toggled={rebuild}
            onToggle={v => setRebuild(v)}
            size="sm"
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', margin: 0 }}>
            {hasExistingRows
              ? `The dataset already has ${dataset?.rows_total} row(s). Rebuilding will clear them and re-run extraction with the updated schema.`
              : 'No rows extracted yet. Rebuilding will start the agentic extraction pipeline.'}
          </p>
        </div>
      </Stack>
    </Modal>
  );
}

// Made with Bob
